import type {State} from '@bitstillery/mithril'

/**
 * The one place a value is handed on as a type nothing checked. The lint rule against unsafe type
 * assertions is off for this file only, so every unchecked boundary stays greppable as a call here.
 *
 * Use it where the shape is guaranteed by something the compiler can't see: a JSON body from our own
 * API or storage, or a generic helper that returns its argument's shape. Anywhere else, narrow with
 * `instanceof`, `typeof` or a type guard instead.
 */
export function unchecked<T>(value: unknown): T {
    return value as T
}

/** `Object.keys` typed by the object's declared keys, which it can't be in general: TS object types aren't exact. */
export function keys_of<T extends object>(value: T): (keyof T & string)[] {
    return Object.keys(value) as (keyof T & string)[]
}

/** `Object.entries` typed by the object's declared keys and values, under the same caveat as `keys_of`. */
export function entries_of<T extends object>(value: T): [keyof T & string, T[keyof T]][] {
    return Object.entries(value) as [keyof T & string, T[keyof T]][]
}

/**
 * A plain object typed as the state proxy it becomes once written to a record key
 * (`$s.cart.items[artkey] = as_state(item)`). A declared key takes a plain value through its signal
 * (`$s.cart.$errors.value = errors`); an index-signature key has no signal to write through.
 */
export function as_state<T extends object>(value: T): State<T> {
    return value as State<T>
}
